import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import type { TicketDto, TicketPriority, TicketsResponse, TicketView } from '@eskoolhub/types';
import { PageHeader } from '../components/AppShell';
import { useToast } from '../components/Toasts';
import { Chip, EmptyState, ErrorState, SkeletonRows } from '../components/ui';
import { api, ApiRequestError } from '../lib/api';
import { formatDateTime } from '../lib/format';

const PRIORITY_TONE: Record<TicketPriority, 'danger' | 'warn' | 'neutral'> = {
  URGENT: 'danger',
  HIGH: 'warn',
  NORMAL: 'neutral',
  LOW: 'neutral',
};

const VIEWS: { label: string; value: TicketView }[] = [
  { label: 'Open', value: 'OPEN' },
  { label: 'Resolved', value: 'RESOLVED' },
  { label: 'All', value: 'ALL' },
];

function isResolved(ticket: TicketDto): boolean {
  return ticket.status === 'RESOLVED' || ticket.status === 'CLOSED';
}

export function TicketsPage() {
  const [params, setParams] = useSearchParams();
  const toast = useToast();
  const queryClient = useQueryClient();

  const view = (params.get('view') as TicketView | null) ?? 'OPEN';
  const priority = params.get('priority');

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['tickets', view, priority],
    queryFn: () =>
      api.get<TicketsResponse>(`/api/tickets?view=${view}${priority ? `&priority=${priority}` : ''}`),
  });

  function setParam(key: string, value: string | null) {
    const next = new URLSearchParams(params);
    if (value === null) next.delete(key);
    else next.set(key, value);
    setParams(next);
  }

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ['tickets'] });
    // The sidebar badge counts open tickets, and the overview KPI reads them too.
    void queryClient.invalidateQueries({ queryKey: ['sidebar-counts'] });
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  }

  const resolve = useMutation({
    mutationFn: (id: string) => api.post<TicketDto>(`/api/tickets/${id}/resolve`),
    onSuccess: (ticket) => {
      invalidate();
      toast.push(`Resolved · ${ticket.subject}`, 'success');
    },
    onError: (err) => toast.push(err instanceof ApiRequestError ? err.message : 'Could not resolve', 'error'),
  });

  const reopen = useMutation({
    mutationFn: (id: string) => api.post<TicketDto>(`/api/tickets/${id}/reopen`),
    onSuccess: (ticket) => {
      invalidate();
      toast.push(`Reopened · ${ticket.subject}`, 'success');
    },
    onError: (err) => toast.push(err instanceof ApiRequestError ? err.message : 'Could not reopen', 'error'),
  });

  const busy = resolve.isPending || reopen.isPending;

  return (
    <>
      <PageHeader
        title="Support tickets"
        meta={
          data
            ? `${data.openCount} open · ${data.urgentCount} urgent · ${data.resolvedCount} resolved`
            : 'Loading…'
        }
        actions={
          <>
            <div className="segmented">
              {VIEWS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  aria-pressed={view === option.value}
                  onClick={() => setParam('view', option.value)}
                  style={{ padding: '10px 16px', flex: 'none' }}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <button
              className={priority === 'URGENT' ? 'btn btn--sm' : 'btn btn--ghost btn--sm'}
              onClick={() => setParam('priority', priority === 'URGENT' ? null : 'URGENT')}
            >
              Urgent only
            </button>
          </>
        }
      />

      <div className="page">
        {error ? (
          <ErrorState
            message={error instanceof ApiRequestError ? error.message : 'Could not load tickets'}
            onRetry={() => void refetch()}
          />
        ) : isPending ? (
          <SkeletonRows count={5} />
        ) : data.items.length === 0 ? (
          <div className="card card--flush">
            <EmptyState
              title={view === 'RESOLVED' ? 'Nothing resolved yet' : 'Inbox zero'}
              message={
                view === 'RESOLVED'
                  ? 'Tickets you resolve will be listed here with the date they were closed.'
                  : 'No open tickets match this filter.'
              }
            />
          </div>
        ) : (
          <div className="card card--flush">
            <table className="table">
              <thead>
                <tr>
                  <th>Subject</th>
                  <th style={{ width: 200 }}>School</th>
                  <th style={{ width: 110 }}>Priority</th>
                  <th style={{ width: 160 }}>Opened</th>
                  <th style={{ width: 210 }}>Resolved</th>
                  <th style={{ width: 110 }} />
                </tr>
              </thead>
              <tbody>
                {data.items.map((ticket) => {
                  const resolved = isResolved(ticket);
                  return (
                    <tr key={ticket.id} style={{ cursor: 'default' }}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <span
                            style={{
                              fontSize: 14,
                              fontWeight: 700,
                              color: resolved ? 'var(--ink-soft)' : undefined,
                            }}
                          >
                            {ticket.subject}
                          </span>
                          {resolved ? <Chip tone="success">Resolved</Chip> : null}
                        </div>
                      </td>
                      <td className="caption">{ticket.schoolName ?? '—'}</td>
                      <td>
                        <Chip tone={resolved ? 'neutral' : PRIORITY_TONE[ticket.priority]}>
                          {ticket.priority.charAt(0) + ticket.priority.slice(1).toLowerCase()}
                        </Chip>
                      </td>
                      <td className="mono caption">{formatDateTime(ticket.openedAt)}</td>
                      <td className="mono caption">
                        {ticket.resolvedAt ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <span style={{ color: 'var(--leaf)', fontWeight: 600 }}>
                              {formatDateTime(ticket.resolvedAt)}
                            </span>
                            {ticket.resolvedByName ? (
                              <span style={{ fontSize: 11.5 }}>by {ticket.resolvedByName}</span>
                            ) : null}
                          </div>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {resolved ? (
                          <button
                            className="btn btn--ghost btn--sm"
                            onClick={() => reopen.mutate(ticket.id)}
                            disabled={busy}
                          >
                            Reopen
                          </button>
                        ) : (
                          <button
                            className="btn btn--soft btn--sm"
                            onClick={() => resolve.mutate(ticket.id)}
                            disabled={busy}
                          >
                            Resolve
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
